import * as i0 from '@angular/core';
import { Input, Component, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

/**
 * A component that displays a loading spinner with three dots that animate in a loop.
 *
 * @example
 * <e11-loader-ellipsis [label]="label" [fontColor]="fontColor" [fullScreen]="fullScreen" [active]="active" [buttonLoader]="buttonLoader" [fade]="fade"></e11-loader-ellipsis>
 */
class E11LoaderEllipsisComponent {
    constructor() {
        /**
         * The label to display next to the loading dots.
         * @type {string}
         * @default 'Loading'
         */
        this.label = 'Loading';
        /**
         * The font color of the label. This can be a color class or a hex value.
         * @type {string}
         * @default 'primary-accent'
         */
        this.fontColor = 'primary-accent';
        /**
         * Whether the loader should be full screen.
         * @type {boolean | undefined}
         * @default false
         */
        this.fullScreen = false;
        /**
         * Whether the loader is active.
         * "active" mean seen, on, displayed,  z-index: 99 to stack above backdrop and other elements
         * @type {boolean | undefined}
         * @default true
         */
        this.active = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderEllipsisComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11LoaderEllipsisComponent, isStandalone: true, selector: "e11-loader-ellipsis", inputs: { label: "label", fontColor: "fontColor", fullScreen: "fullScreen", active: "active", buttonLoader: "buttonLoader", fade: "fade" }, ngImport: i0, template: `
    <div class="e11-loading-ellipsis-container" [ngClass]="{ 'e11-loader-dots-fullscreen': fullScreen, 'e11-loader-dots-active': active }">
      <div class="e11-loading-ellipsis e11-w-full e11-text-center" [ngClass]="{ 'button-loader': buttonLoader, 'e11-loader-dots-fade': fade }">
        <span class="e11-relative">
          <span
            class="label e11-mr-1"
            [ngClass]="{
          'e11-text-skin-primary': fontColor === 'primary',
          'e11-text-skin-primary-accent': fontColor === 'primary-accent',
          'e11-text-skin-secondary': fontColor === 'secondary',
          'e11-text-skin-light': fontColor === 'neutral',
          'e11-text-skin-white': fontColor === 'white',
        }"
            >{{ label }}</span
          >
          <span class="subtext e11-text-sm e11-text-skin-light">...almost there</span>
        </span>

        <div
          class="e11-loading-ellipsis-dot e11-rounded-lg e11-inline-block e11-mr-1 e11-w-2 e11-h-2"
          [ngClass]="{
          'e11-bg-skin-primary': fontColor === 'primary',
          'e11-bg-skin-primary-accent': fontColor === 'primary-accent',
          'e11-bg-skin-secondary': fontColor === 'secondary',
          'e11-bg-skin-grey/50': fontColor === 'neutral',
          'e11-bg-skin-white': fontColor === 'white',
        }"
        ></div>
        <div
          class="e11-loading-ellipsis-dot e11-rounded-lg e11-inline-block e11-mr-1 e11-w-2 e11-h-2"
          [ngClass]="{
          'e11-bg-skin-primary': fontColor === 'primary',
          'e11-bg-skin-primary-accent': fontColor === 'primary-accent',
          'e11-bg-skin-secondary': fontColor === 'secondary',
          'e11-bg-skin-grey/50': fontColor === 'neutral',
          'e11-bg-skin-white': fontColor === 'white',
        }"
        ></div>
        <div
          class="e11-loading-ellipsis-dot e11-rounded-lg e11-inline-block e11-mr-1 e11-w-2 e11-h-2"
          [ngClass]="{
          'e11-bg-skin-primary': fontColor === 'primary',
          'e11-bg-skin-primary-accent': fontColor === 'primary-accent',
          'e11-bg-skin-secondary': fontColor === 'secondary',
          'e11-bg-skin-grey/50': fontColor === 'neutral',
          'e11-bg-skin-white': fontColor === 'white',
        }"
        ></div>
      </div>
    </div>
  `, isInline: true, styles: [".e11-loading-ellipsis-container.e11-loader-ellipsis-fullscreen{position:fixed;display:flex;width:0;height:0;inset:-1000px 0 0;align-items:center;justify-content:center;opacity:0;z-index:-2;transition:opacity .4s ease-in-out 0s,z-index 0s linear .4s,top 0s linear .4s,width 0s linear .4s,height 0s linear .4s}.e11-loading-ellipsis-container.e11-loader-ellipsis-fullscreen.e11-loader-ellipsis-active{opacity:1;z-index:99;top:0;width:100%;height:100%;transition:opacity 0s ease-in-out 0s,z-index 0s linear 0s,top 0s linear 0s,width 0s linear 0s,height 0s linear 0s}.e11-loading-ellipsis-container .e11-loader-ellipsis-fade{opacity:0;z-index:-2;transition:opacity 1s ease-in-out 0s,z-index 0s ease-in-out 1.2s}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-dot{animation:dot-keyframes 1.5s infinite ease-in-out}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-dot:nth-child(2){animation-delay:.5s}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-dot:nth-child(3){animation-delay:1s}.e11-loading-ellipsis-container .e11-loading-ellipsis.button-loader .dot{border-radius:4px;height:1rem;width:4px}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-title{animation:fadeUp 2s ease-in forwards,fadeIn 2s ease-in forwards}@keyframes dot-keyframes{0%{opacity:.4;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}to{opacity:.4;transform:scale(1)}}@keyframes fadeUp{0%{top:3rem;opacity:0}to{top:1rem;opacity:1}}@keyframes fadeIn{0%{opacity:.1}to{opacity:1}}.e11-loading-ellipsis-container .label{font-size:38px;font-family:Poppins;font-weight:700}.fill-primary-accent{fill:#0080d3}.fill-secondary{fill:#d80b8b}.subtext{position:absolute;bottom:-13px;left:2rem}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderEllipsisComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-loader-ellipsis', standalone: true, imports: [CommonModule], template: `
    <div class="e11-loading-ellipsis-container" [ngClass]="{ 'e11-loader-dots-fullscreen': fullScreen, 'e11-loader-dots-active': active }">
      <div class="e11-loading-ellipsis e11-w-full e11-text-center" [ngClass]="{ 'button-loader': buttonLoader, 'e11-loader-dots-fade': fade }">
        <span class="e11-relative">
          <span
            class="label e11-mr-1"
            [ngClass]="{
          'e11-text-skin-primary': fontColor === 'primary',
          'e11-text-skin-primary-accent': fontColor === 'primary-accent',
          'e11-text-skin-secondary': fontColor === 'secondary',
          'e11-text-skin-light': fontColor === 'neutral',
          'e11-text-skin-white': fontColor === 'white',
        }"
            >{{ label }}</span
          >
          <span class="subtext e11-text-sm e11-text-skin-light">...almost there</span>
        </span>

        <div
          class="e11-loading-ellipsis-dot e11-rounded-lg e11-inline-block e11-mr-1 e11-w-2 e11-h-2"
          [ngClass]="{
          'e11-bg-skin-primary': fontColor === 'primary',
          'e11-bg-skin-primary-accent': fontColor === 'primary-accent',
          'e11-bg-skin-secondary': fontColor === 'secondary',
          'e11-bg-skin-grey/50': fontColor === 'neutral',
          'e11-bg-skin-white': fontColor === 'white',
        }"
        ></div>
        <div
          class="e11-loading-ellipsis-dot e11-rounded-lg e11-inline-block e11-mr-1 e11-w-2 e11-h-2"
          [ngClass]="{
          'e11-bg-skin-primary': fontColor === 'primary',
          'e11-bg-skin-primary-accent': fontColor === 'primary-accent',
          'e11-bg-skin-secondary': fontColor === 'secondary',
          'e11-bg-skin-grey/50': fontColor === 'neutral',
          'e11-bg-skin-white': fontColor === 'white',
        }"
        ></div>
        <div
          class="e11-loading-ellipsis-dot e11-rounded-lg e11-inline-block e11-mr-1 e11-w-2 e11-h-2"
          [ngClass]="{
          'e11-bg-skin-primary': fontColor === 'primary',
          'e11-bg-skin-primary-accent': fontColor === 'primary-accent',
          'e11-bg-skin-secondary': fontColor === 'secondary',
          'e11-bg-skin-grey/50': fontColor === 'neutral',
          'e11-bg-skin-white': fontColor === 'white',
        }"
        ></div>
      </div>
    </div>
  `, styles: [".e11-loading-ellipsis-container.e11-loader-ellipsis-fullscreen{position:fixed;display:flex;width:0;height:0;inset:-1000px 0 0;align-items:center;justify-content:center;opacity:0;z-index:-2;transition:opacity .4s ease-in-out 0s,z-index 0s linear .4s,top 0s linear .4s,width 0s linear .4s,height 0s linear .4s}.e11-loading-ellipsis-container.e11-loader-ellipsis-fullscreen.e11-loader-ellipsis-active{opacity:1;z-index:99;top:0;width:100%;height:100%;transition:opacity 0s ease-in-out 0s,z-index 0s linear 0s,top 0s linear 0s,width 0s linear 0s,height 0s linear 0s}.e11-loading-ellipsis-container .e11-loader-ellipsis-fade{opacity:0;z-index:-2;transition:opacity 1s ease-in-out 0s,z-index 0s ease-in-out 1.2s}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-dot{animation:dot-keyframes 1.5s infinite ease-in-out}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-dot:nth-child(2){animation-delay:.5s}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-dot:nth-child(3){animation-delay:1s}.e11-loading-ellipsis-container .e11-loading-ellipsis.button-loader .dot{border-radius:4px;height:1rem;width:4px}.e11-loading-ellipsis-container .e11-loading-ellipsis .e11-loading-ellipsis-title{animation:fadeUp 2s ease-in forwards,fadeIn 2s ease-in forwards}@keyframes dot-keyframes{0%{opacity:.4;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}to{opacity:.4;transform:scale(1)}}@keyframes fadeUp{0%{top:3rem;opacity:0}to{top:1rem;opacity:1}}@keyframes fadeIn{0%{opacity:.1}to{opacity:1}}.e11-loading-ellipsis-container .label{font-size:38px;font-family:Poppins;font-weight:700}.fill-primary-accent{fill:#0080d3}.fill-secondary{fill:#d80b8b}.subtext{position:absolute;bottom:-13px;left:2rem}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], fontColor: [{
                type: Input
            }], fullScreen: [{
                type: Input
            }], active: [{
                type: Input
            }], buttonLoader: [{
                type: Input
            }], fade: [{
                type: Input
            }] } });

class E11LoaderEllipsisModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderEllipsisModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderEllipsisModule, imports: [E11LoaderEllipsisComponent], exports: [E11LoaderEllipsisComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderEllipsisModule, imports: [E11LoaderEllipsisComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11LoaderEllipsisModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11LoaderEllipsisComponent],
                    exports: [E11LoaderEllipsisComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11LoaderEllipsisComponent, E11LoaderEllipsisModule };
